// KindergartenModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';

const KindergartenModal = ({ 
  isOpen, 
  onClose, 
  kindergartenData, 
  onSubmit,
  viewMode = false,
  isEditPossible = true,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    edrpou: '',
    name: '',
    description: '',
    address: '',
    district: '',
    phone: '',
    email: '',
    directorName: '',
    licenseNumber: '',
    licenseIssueDate: '',
    capacity: '',
    ageGroups: [],
    workingHours: '07:00-19:00',
    website: ''
  });

  const districts = [
    'Голосіївський',
    'Дарницький', 
    'Деснянський',
    'Дніпровський',
    'Оболонський',
    'Печерський',
    'Подільський',
    'Святошинський',
    'Солом\'янський',
    'Шевченківський'
  ];

  const ageGroupOptions = ['1-3', '3-4', '4-5', '5-6', '6-7'];

  useEffect(() => {
    if (kindergartenData) {
      setFormData({
        edrpou: kindergartenData.edrpou || '',
        name: kindergartenData.name || '',
        description: kindergartenData.description || '',
        address: kindergartenData.address || '',
        district: kindergartenData.district || '',
        phone: kindergartenData.phone || '',
        email: kindergartenData.email || '',
        directorName: kindergartenData.directorName || '',
        licenseNumber: kindergartenData.licenseNumber || '',
        licenseIssueDate: kindergartenData.licenseIssueDate || '',
        capacity: kindergartenData.capacity || '',
        ageGroups: kindergartenData.ageGroups || [],
        workingHours: kindergartenData.workingHours || '07:00-19:00',
        website: kindergartenData.website || ''
      });
    } else {
      setFormData({
        edrpou: '',
        name: '',
        description: '',
        address: '',
        district: '',
        phone: '',
        email: '',
        directorName: '',
        licenseNumber: '',
        licenseIssueDate: '',
        capacity: '',
        ageGroups: [],
        workingHours: '07:00-19:00',
        website: ''
      });
    }
  }, [kindergartenData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'select-multiple') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({
        ...prev,
        [name]: selectedOptions
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleAgeGroupToggle = (ageGroup) => {
    setFormData(prev => ({
      ...prev,
      ageGroups: prev.ageGroups.includes(ageGroup)
        ? prev.ageGroups.filter(ag => ag !== ageGroup)
        : [...prev.ageGroups, ageGroup]
    }));
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {viewMode 
            ? 'Перегляд дитячого садочку' 
            : kindergartenData 
              ? 'Редагувати дитячий садочок' 
              : 'Додати новий дитячий садочок'
          }
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ЄДРПОУ *</Form.Label>
                <Form.Control
                  type="text"
                  name="edrpou"
                  value={formData.edrpou}
                  onChange={handleChange}
                  required={!viewMode}
                  placeholder="12345678"
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                  pattern="[0-9]{8,10}"
                  maxLength={10}
                />
                <Form.Text className="text-muted">
                  8-10 цифр
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Район *</Form.Label>
                <Form.Select
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  required={!viewMode}
                  disabled={viewMode || !isEditPossible || loading}
                >
                  <option value="">Оберіть район</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Назва закладу *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required={!viewMode}
              placeholder="Наприклад: ЧДНЗ 'Малятко'"
              disabled={viewMode || !isEditPossible || loading}
              readOnly={viewMode}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Адреса *</Form.Label>
            <Form.Control
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required={!viewMode}
              placeholder="м. Київ, вул. Прикладна, 1"
              disabled={viewMode || !isEditPossible || loading}
              readOnly={viewMode}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Опис закладу</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Опишіть особливості садочку, програми розвитку тощо"
              disabled={viewMode || !isEditPossible || loading}
              readOnly={viewMode}
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Телефон</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+380441234567"
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="info@sadok.kyiv.ua"
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ПІБ директора</Form.Label>
                <Form.Control
                  type="text"
                  name="directorName"
                  value={formData.directorName}
                  onChange={handleChange}
                  placeholder="Іванова Марія Петрівна"
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Графік роботи</Form.Label>
                <Form.Control
                  type="text"
                  name="workingHours"
                  value={formData.workingHours}
                  onChange={handleChange}
                  placeholder="07:00-19:00"
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Номер ліцензії</Form.Label>
                <Form.Control
                  type="text"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  placeholder="ЛЦ №123456"
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Дата видачі ліцензії</Form.Label>
                <Form.Control
                  type="date"
                  name="licenseIssueDate"
                  value={formData.licenseIssueDate}
                  onChange={handleChange}
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Місткість</Form.Label>
                <Form.Control
                  type="number"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  placeholder="75"
                  min="0"
                  max="1000"
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Веб-сайт</Form.Label>
                <Form.Control
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://sadok.kyiv.ua"
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Вікові групи</Form.Label>
            <div>
              {ageGroupOptions.map(ageGroup => (
                <Form.Check
                  key={ageGroup}
                  inline
                  type="checkbox"
                  label={ageGroup}
                  checked={formData.ageGroups.includes(ageGroup)}
                  onChange={() => handleAgeGroupToggle(ageGroup)}
                  disabled={viewMode || !isEditPossible || loading}
                  readOnly={viewMode}
                />
              ))}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {viewMode ? 'Закрити' : 'Скасувати'}
          </Button>
          {!viewMode && isEditPossible && (
            <Button variant="primary" type="submit" disabled={loading}>
              {loading && <Spinner animation="border" size="sm" className="me-2" />}
              {kindergartenData ? 'Зберегти зміни' : 'Додати садочок'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default KindergartenModal;